// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, mergeMap, Observable, of } from "rxjs"
import { catchError, filter } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as ToolbarActions from "../actions"
import { showSnackBar } from "../../../snackbar/actions"
import * as TimelineSlice from "../../slices/timeline"
import { executorMutation, Init, timelineService } from "../../../../app/rpc"
import {
  CreateTimelineMarkerRequestSchema,
  TimelineMarker,
} from "../../../../generated/wisetime/user/timeline/v4/timeline_pb"

type CreateTimelineMarkerRpc = (request: Init<typeof CreateTimelineMarkerRequestSchema>) => Observable<TimelineMarker>

export const createTimelineMarker = (createTimelineMarkerRpc: CreateTimelineMarkerRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(ToolbarActions.createTimelineMarker.match),
      mergeMap(action => createTimelineMarkerRpc(action.payload)
        .pipe(
          mergeMap(timelineMarker =>
            concat(
              of(TimelineSlice.addTimelineMarker(timelineMarker)),
              of(showSnackBar({
                message: t({ id: "msg_timeline_marker_added", message: "Marker added." }),
                messageType: "SUCCESS",
              }))
            )
          ),
          catchError(() => of(showSnackBar({
            message: t({ id: "msg_timeline_marker_failed", message: "Failed to add marker. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
            messageType: "ERROR",
          }))),
        )
      ),
    )

const createTimelineMarkerRpc = (request: Init<typeof CreateTimelineMarkerRequestSchema>): Observable<TimelineMarker> =>
  executorMutation(() => timelineService.createTimelineMarker(create(CreateTimelineMarkerRequestSchema, request)))

export default createTimelineMarker(createTimelineMarkerRpc)
